import { mapActions, mapState } from "pinia";

import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";

// Lazy store resolution: a module-scope import here closes a cycle
// (stores -> components -> mixins -> classes). See scripts/find-import-cycles.py.
// eslint-disable-next-line global-require
const useDynamicComponentEditorStore = (...args) => require("%/dynamic-component-editor/stores/dynamic-component-editor").default(...args);
// eslint-disable-next-line global-require
const useBrandingStore = (...args) => require("%/dynamic-component-editor/stores/branding-store").default(...args);


const CanvasStyleMixinStaging = {
  computed: {
    ...mapState(useDynamicComponentEditorStore, [
      "elementsToFocus",
      "parentsToFocus",
      "childrenToFocus",
      "selectionSet",
      "isDraggingOnBuilder",
      "indicatorIsInLayoutWithIdentifier",
    ]),
    hasABorder() {
      return this.primaryHoverBorder || this.secondaryHoverBorder || this.selectedHoverBorder;
    },
    mouseOverElement() {
      return this.mouseOver || this.mouseOverActions;
    },
    isElementToFocus() {
      return this.elementsToFocus.includes(this.identifier);
    },
    isElementToFocusAndMouseIsOverElement() {
      return this.isElementToFocus && this.mouseOverElement;
    },
    primaryHoverBorder() {
      if (this.isElementToFocus) {
        return true;
      } else if (this.indicatorIsInLayoutWithIdentifier === this.identifier) {
        return true;
      } else if (!this.isDraggingOnBuilder) {
        return this.isElementToFocusAndMouseIsOverElement;
      }
      return false;
    },
    isParentToFocus() {
      return this.parentsToFocus.includes(this.identifier) && this.isLayout;
    },
    isChildToFocus() {
      return this.childrenToFocus.includes(this.identifier);
    },
    secondaryHoverBorder() {
      return this.isParentToFocus || this.isChildToFocus;
    },
    selectedHoverBorder() {
      // O(1) per-key Set membership instead of an O(N) global array scan.
      return this.selectionSet.has(String(this.identifier));
    },
    isDraggingOverLayout() {
      return this.isDraggingOver && this.isLayout;
    },
    hasOverride() {
      if (useBrandingStore().currentlyInspectedBrandingElementVariant) {
        return false;
      }
      const elementInstance = DynamicElement.get(this.identifier);
      if (!elementInstance?.elementDef) {
        return false;
      }
      return elementInstance.hasAnyOverrides;
    },
    showPrimaryHoverBorder() {
      return this.editMode && this.primaryHoverBorder;
    },
    showSecondaryHoverBorder() {
      return this.editMode && this.secondaryHoverBorder && !this.primaryHoverBorder && !this.selectedHoverBorder;
    },
    showSelectedHoverBorder() {
      return this.editMode && this.selectedHoverBorder && !this.primaryHoverBorder;
    },
    showOverriddenBorder() {
      return this.editMode && this.hasOverride && (this.primaryHoverBorder || this.secondaryHoverBorder || this.selectedHoverBorder);
    },
    classList() {
      return {
        "i-dnd": true,
        "drag-over-layout": this.editMode && this.isDraggingOverLayout && this.allowsDrop,
        "dragging-over": this.editMode && this.isDraggingOver,
        "i-dynamic-component": true,
      };
    },
  },
  methods: {
    ...mapActions(useDynamicComponentEditorStore, [
      "allowsDrop",
      "getElement",
    ]),
    clearHoverBorders() {
      useDynamicComponentEditorStore().clearElementBorder(this.identifier);
    },
    addHoverBorder() {
      let type;
      if (this.showPrimaryHoverBorder) {
        type = "primary";
      } else if (this.showSecondaryHoverBorder) {
        type = "secondary";
      } else if (this.showSelectedHoverBorder) {
        type = "selected";
      } else {
        return;
      }

      if (this.showOverriddenBorder) {
        type += "-overwritten";
      }

      useDynamicComponentEditorStore().toggleElementBorder(this.identifier, type, {
        element: this.$el,
      });
    },
  },
  watch: {
    showPrimaryHoverBorder() {
      this.clearHoverBorders();
      this.addHoverBorder();
    },
    showSecondaryHoverBorder() {
      this.clearHoverBorders();
      this.addHoverBorder();
    },
    showSelectedHoverBorder() {
      this.clearHoverBorders();
      this.addHoverBorder();
    },
  },
};

export { CanvasStyleMixinStaging };
